<template>
  <div class="mcc-root">
    <div class="mcc-title">
      <img src="../assets/tool-logo.png" alt="multi counter">
    </div>

    <div class="mcc-list">
      <div
        v-for="(counter, index) in counters"
        :key="counter.id"
        class="mcc-card"
      >
        <div class="mcc-card-top">
          <span class="mcc-index">#{{ index + 1 }}</span>
          <input
            v-model="counter.label"
            class="mcc-input mcc-input-label"
            type="text"
            placeholder="ラベル"
            @input="persist"
          >
          <input
            v-model="counter.unit"
            class="mcc-input mcc-input-unit"
            type="text"
            placeholder="単位"
            @input="persist"
          >
          <button
            class="mcc-btn-icon mcc-btn-remove"
            :disabled="counters.length <= 1"
            title="このカウンターを削除"
            @click="removeCounter(index)"
          >×</button>
        </div>

        <div class="mcc-card-bottom">
          <button
            class="mcc-btn mcc-btn-step"
            title="-1"
            @click="decrement(index)"
          >−</button>
          <div class="mcc-count">
            <span class="mcc-count-num">{{ counter.count }}</span>
            <span v-if="counter.unit" class="mcc-count-unit">{{ counter.unit }}</span>
          </div>
          <button
            class="mcc-btn mcc-btn-step"
            title="+1"
            @click="increment(index)"
          >＋</button>
          <button
            class="mcc-btn mcc-btn-reset"
            title="0 にリセット"
            @click="reset(index)"
          >リセット</button>
        </div>
      </div>
    </div>

    <a
      class="mcc-design-link"
      href="https://ruri.info/tool/multicounter"
      target="_blank"
      rel="noopener noreferrer"
    >
      デザインページを開く
      <span class="mcc-design-link-arrow" aria-hidden="true">↗</span>
    </a>
    <p class="mcc-design-note">
      カウンターの追加やデザイン変更はデザインページで行います
    </p>
  </div>
</template>

<script>
const STORAGE_KEY = 'multiCounters'

function createDefaultCounter(index = 0) {
  return {
    id: `c${Date.now()}${Math.floor(Math.random() * 1000)}`,
    label: `カウンター${index + 1}`,
    unit: '',
    count: 0,
    backgroundShape: 'rounded',
    backgroundColor: 'sakura',
    textColor: '#8B4C63'
  }
}

function loadCounters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return [createDefaultCounter(0)]
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [createDefaultCounter(0)]
    }
    return parsed.map((c, i) => ({
      ...createDefaultCounter(i),
      ...c
    }))
  } catch (e) {
    return [createDefaultCounter(0)]
  }
}

export default {
  data() {
    return {
      counters: loadCounters()
    }
  },
  methods: {
    persist() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.counters))
    },
    increment(index) {
      this.counters[index].count++
      this.persist()
    },
    decrement(index) {
      this.counters[index].count--
      this.persist()
    },
    reset(index) {
      this.counters[index].count = 0
      this.persist()
    },
    removeCounter(index) {
      if (this.counters.length <= 1) return
      this.counters.splice(index, 1)
      this.persist()
    },
    handleStorageChange(event) {
      if (event.key !== STORAGE_KEY) return
      this.counters = loadCounters()
    }
  },
  mounted() {
    window.addEventListener('storage', this.handleStorageChange)
    // Only seed storage when nothing is there yet so we don't overwrite
    // whatever the customization page or design page may have written.
    if (!localStorage.getItem(STORAGE_KEY)) {
      this.persist()
    }
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange)
  }
}
</script>

<style scoped>
/* OBS dock controller — light, compact layout that matches the customization
 * page's tone (white cards on a soft neutral background). */
.mcc-root {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background-color: #f5f5f7;
  color: #2c2f36;
  min-height: 100vh;
  box-sizing: border-box;
  font-size: 13px;
}

.mcc-title {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2px;
}

.mcc-title img {
  width: 160px;
  height: auto;
}

.mcc-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 320px;
}

/* Each counter is two rows: inputs on top, ops on the bottom. */
.mcc-card {
  background-color: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.mcc-card-top {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mcc-index {
  font-size: 11px;
  font-weight: bold;
  color: #4c5c8a;
  min-width: 22px;
  text-align: center;
  letter-spacing: 0.5px;
}

.mcc-input {
  background: #fafbfc;
  border: 1px solid #d6d9e0;
  border-radius: 4px;
  color: #2c2f36;
  font-size: 12px;
  padding: 4px 6px;
  box-sizing: border-box;
  min-width: 0;
  outline: none;
  transition: border-color 0.15s;
}

.mcc-input:focus {
  border-color: #4c5c8a;
  background: #ffffff;
}

.mcc-input::placeholder {
  color: #aab0bd;
}

.mcc-input-label {
  flex: 1 1 auto;
}

.mcc-input-unit {
  flex: 0 0 56px;
  text-align: center;
}

.mcc-card-bottom {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mcc-count {
  flex: 1 1 auto;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 3px;
  background: #f5f5f7;
  border-radius: 4px;
  padding: 4px 8px;
  min-height: 28px;
  font-variant-numeric: tabular-nums;
}

.mcc-count-num {
  font-size: 18px;
  font-weight: bold;
  color: #2c2f36;
}

.mcc-count-unit {
  font-size: 12px;
  font-weight: bold;
  color: #4a4f5c;
}

.mcc-btn {
  background: #4c5c8a;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.mcc-btn:hover:not(:disabled) {
  background: #3b4b74;
}

.mcc-btn-step {
  width: 30px;
  height: 28px;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  font-weight: bold;
}

.mcc-btn-reset {
  background: transparent;
  color: #6c7084;
  border: 1px solid #d6d9e0;
  font-size: 11px;
  padding: 4px 8px;
}

.mcc-btn-reset:hover:not(:disabled) {
  background: #f0f0f4;
  color: #2c2f36;
}

.mcc-btn-icon {
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #aab0bd;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}

.mcc-btn-icon:hover:not(:disabled) {
  background: #fde8e8;
  color: #d9534f;
}

.mcc-btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.mcc-design-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  max-width: 320px;
  margin-top: 4px;
  padding: 8px 12px;
  background: #ffffff;
  color: #4c5c8a;
  border: 1px solid #4c5c8a;
  border-radius: 6px;
  font-size: 12px;
  font-weight: bold;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  box-sizing: border-box;
}

.mcc-design-link:hover {
  background: #4c5c8a;
  color: #ffffff;
}

.mcc-design-link-arrow {
  font-size: 13px;
  line-height: 1;
}

.mcc-design-note {
  font-size: 11px;
  color: #8a8e99;
  text-align: center;
  margin: 0;
  max-width: 320px;
  line-height: 1.4;
}
</style>
